import React from 'react';
import { ChevronRight, ChevronLeft, Clock, Users, TrendingUp, CheckCircle, Timer, BarChart3 } from 'lucide-react';
import TrialConnxLogo from '../assets/tco-logo-text.svg';

const ProcessFlow = ({ 
  currentStep, 
  steps, 
  assessmentData, 
  updateAssessmentData, 
  handleNext, 
  handlePrevious, 
  isStepValid 
}) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={TrialConnxLogo} alt="TrialConnx" className="h-6 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">StudySpeed Calculator</h1>
        </div>

        {/* Progress Bar */}
        <div className="mb-6 sm:mb-8">
          {/* Progress Stats */}
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 mx-4 sm:mx-0 hover-lift fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-primary-600 rounded-full animate-pulse mr-3 flex-shrink-0"></div>
                <span className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                  Step {currentStep + 1} of {steps.length}: <span className="block sm:inline">{steps[currentStep].title}</span>
                </span>
              </div>
              <div className="text-sm text-gray-500 sm:text-right">
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full progress-bar"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              ></div>
            </div>
            
            {/* Step Icons */}
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300 ${
                    index <= currentStep 
                      ? 'bg-primary-600 text-white shadow-lg' 
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {index < currentStep ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className={`text-xs mt-2 text-center max-w-16 transition-colors duration-300 ${
                    index <= currentStep ? 'text-primary-600 font-medium' : 'text-gray-400'
                  }`}>
                    <step.icon className="w-4 h-4 mx-auto mb-1" />
                    <span className="hidden md:block">{step.title.split(' ')[0]}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Time Estimate */}
            <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-100">
              <Timer className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm text-gray-500">
                Estimated time remaining: {Math.max(1, (steps.length - currentStep - 1))} minute{(steps.length - currentStep - 1) !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mx-4 sm:mx-0 hover-lift fade-in">
          {/* Step 1: Site Information */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <Users className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">Site Information</h2>
                <p className="text-gray-600 mt-2">Tell us about your research site (optional)</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.siteInfo.organizationName}
                    onChange={(e) => updateAssessmentData('siteInfo', 'organizationName', e.target.value)}
                    placeholder="Your organization name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site Type
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.siteInfo.siteType}
                    onChange={(e) => updateAssessmentData('siteInfo', 'siteType', e.target.value)}
                  >
                    <option value="">Select site type</option>
                    <option value="Academic Medical Center">Academic Medical Center</option>
                    <option value="Private Practice">Private Practice</option>
                    <option value="Hospital System">Hospital System</option>
                    <option value="Independent Research Site">Independent Research Site</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Geographic Region
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.siteInfo.region}
                    onChange={(e) => updateAssessmentData('siteInfo', 'region', e.target.value)}
                  >
                    <option value="">Select region</option>
                    <option value="North America">North America</option>
                    <option value="Europe">Europe</option>
                    <option value="Asia-Pacific">Asia-Pacific</option>
                    <option value="Latin America">Latin America</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Annual Study Volume: {assessmentData.siteInfo.annualVolume} studies
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="50"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.siteInfo.annualVolume}
                    onChange={(e) => updateAssessmentData('siteInfo', 'annualVolume', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>1</span>
                    <span>50+</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Response Times */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <Clock className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">Current Response Times</h2>
                <p className="text-gray-600 mt-2">How quickly does your site respond to feasibility requests?</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Average Feasibility Response Time: {assessmentData.responseTimes.averageResponse} days
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.responseTimes.averageResponse}
                    onChange={(e) => updateAssessmentData('responseTimes', 'averageResponse', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>1 day</span>
                    <span>30 days</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Simple Studies Response Time: {assessmentData.responseTimes.simpleStudies} days
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="15"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.responseTimes.simpleStudies}
                    onChange={(e) => updateAssessmentData('responseTimes', 'simpleStudies', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>1 day</span>
                    <span>15 days</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complex Studies Response Time: {assessmentData.responseTimes.complexStudies} days
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="45"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.responseTimes.complexStudies}
                    onChange={(e) => updateAssessmentData('responseTimes', 'complexStudies', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>5 days</span>
                    <span>45 days</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rush Request Response Time: {assessmentData.responseTimes.rushRequests} days
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="7"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.responseTimes.rushRequests}
                    onChange={(e) => updateAssessmentData('responseTimes', 'rushRequests', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>1 day</span>
                    <span>7 days</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-primary-50 rounded-lg p-4 mt-6 hover:bg-primary-100 transition-colors duration-200">
                <h3 className="font-medium text-primary-900 mb-2">Industry Benchmarks</h3>
                <div className="text-sm text-primary-700 space-y-1">
                  <div>• Simple studies: 3-5 days (excellent), 6-10 days (good)</div>
                  <div>• Complex studies: 7-14 days (excellent), 15-21 days (good)</div>
                  <div>• Rush requests: 1-2 days (excellent), 3-4 days (good)</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Staff & Resources */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <Users className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">Staff & Resources</h2>
                <p className="text-gray-600 mt-2">Tell us about your team and current capabilities</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    FTE Staff Dedicated to Feasibility: {assessmentData.staffResources.dedicatedFTE} FTE
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="10"
                    step="0.5"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.staffResources.dedicatedFTE}
                    onChange={(e) => updateAssessmentData('staffResources', 'dedicatedFTE', parseFloat(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>0.5</span>
                    <span>10+</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level *
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.staffResources.experienceLevel}
                    onChange={(e) => updateAssessmentData('staffResources', 'experienceLevel', e.target.value)}
                    required
                  >
                    <option value="">Select experience level</option>
                    <option value="Junior <2 years">Junior (&lt;2 years)</option>
                    <option value="Mid-level 2-5 years">Mid-level (2-5 years)</option>
                    <option value="Senior 5+ years">Senior (5+ years)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Tools Used
                  </label>
                  <div className="space-y-2">
                    {['Excel/Manual', 'Basic CTMS', 'Advanced CTMS', 'Custom Database', 'Other'].map(tool => (
                      <label key={tool} className="flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 touch-manipulation"
                          checked={assessmentData.staffResources.currentTools.includes(tool)}
                          onChange={(e) => {
                            const tools = assessmentData.staffResources.currentTools;
                            if (e.target.checked) {
                              updateAssessmentData('staffResources', 'currentTools', [...tools, tool]);
                            } else {
                              updateAssessmentData('staffResources', 'currentTools', tools.filter(t => t !== tool));
                            }
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{tool}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Peak Capacity Handling *
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.staffResources.peakCapacity}
                    onChange={(e) => updateAssessmentData('staffResources', 'peakCapacity', e.target.value)}
                    required
                  >
                    <option value="">Select capacity level</option>
                    <option value="Often overwhelmed">Often overwhelmed</option>
                    <option value="Sometimes stretched">Sometimes stretched</option>
                    <option value="Usually manageable">Usually manageable</option>
                    <option value="Excess capacity">Excess capacity</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Study Portfolio */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <TrendingUp className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">Study Portfolio</h2>
                <p className="text-gray-600 mt-2">What types of studies does your site typically conduct?</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Therapeutic Areas *
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {['Oncology', 'Cardiology', 'Neurology', 'Infectious Disease', 'Rare Disease', 'Endocrinology', 'Psychiatry', 'Dermatology', 'Other'].map(area => (
                      <label key={area} className="flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 touch-manipulation"
                          checked={assessmentData.studyPortfolio.therapeuticAreas.includes(area)}
                          onChange={(e) => {
                            const areas = assessmentData.studyPortfolio.therapeuticAreas;
                            if (e.target.checked) {
                              updateAssessmentData('studyPortfolio', 'therapeuticAreas', [...areas, area]);
                            } else {
                              updateAssessmentData('studyPortfolio', 'therapeuticAreas', areas.filter(a => a !== area));
                            }
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Study Phases *
                  </label>
                  <div className="space-y-2">
                    {['Phase I', 'Phase II', 'Phase III', 'Phase IV', 'Post-market'].map(phase => (
                      <label key={phase} className="flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 touch-manipulation"
                          checked={assessmentData.studyPortfolio.studyPhases.includes(phase)}
                          onChange={(e) => {
                            const phases = assessmentData.studyPortfolio.studyPhases;
                            if (e.target.checked) {
                              updateAssessmentData('studyPortfolio', 'studyPhases', [...phases, phase]);
                            } else {
                              updateAssessmentData('studyPortfolio', 'studyPhases', phases.filter(p => p !== phase));
                            }
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{phase}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    International Studies: {assessmentData.studyPortfolio.internationalPercentage}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.studyPortfolio.internationalPercentage}
                    onChange={(e) => updateAssessmentData('studyPortfolio', 'internationalPercentage', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>0%</span>
                    <span>100%</span>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Average Enrollment Targets *
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.studyPortfolio.enrollmentTargets}
                    onChange={(e) => updateAssessmentData('studyPortfolio', 'enrollmentTargets', e.target.value)}
                    required
                  >
                    <option value="">Select enrollment range</option>
                    <option value="<50">&lt;50 patients</option>
                    <option value="50-100">50-100 patients</option>
                    <option value="100-500">100-500 patients</option>
                    <option value="500+">500+ patients</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Process Assessment */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <BarChart3 className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900">Process Assessment</h2>
                <p className="text-gray-600 mt-2">How are your feasibility processes currently structured?</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Documentation Process *
                  </label>
                  <select
                    className="w-full px-4 py-4 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors touch-manipulation"
                    value={assessmentData.processAssessment.documentationProcess}
                    onChange={(e) => updateAssessmentData('processAssessment', 'documentationProcess', e.target.value)}
                    required
                  >
                    <option value="">Select documentation level</option>
                    <option value="Fully manual">Fully manual</option>
                    <option value="Partially automated">Partially automated</option>
                    <option value="Mostly automated">Mostly automated</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Approval Workflow Stages: {assessmentData.processAssessment.approvalStages}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="8"
                    className="w-full h-3 sm:h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider touch-manipulation"
                    value={assessmentData.processAssessment.approvalStages}
                    onChange={(e) => updateAssessmentData('processAssessment', 'approvalStages', parseInt(e.target.value))}
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>1 stage</span>
                    <span>8+ stages</span>
                  </div>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Common Delays
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Staff availability', 'Document review', 'Regulatory questions', 'Budget negotiations', 'PI availability', 'IRB coordination'].map(delay => (
                      <label key={delay} className="flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 touch-manipulation"
                          checked={assessmentData.processAssessment.commonDelays.includes(delay)}
                          onChange={(e) => {
                            const delays = assessmentData.processAssessment.commonDelays;
                            if (e.target.checked) {
                              updateAssessmentData('processAssessment', 'commonDelays', [...delays, delay]);
                            } else {
                              updateAssessmentData('processAssessment', 'commonDelays', delays.filter(d => d !== delay));
                            }
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">{delay}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-8 pt-6 border-t border-gray-200 gap-4">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className={`flex items-center justify-center w-full sm:w-auto px-6 py-4 sm:py-3 rounded-lg font-medium transition-all duration-200 touch-manipulation ${
                currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 bg-white border border-gray-300 hover:scale-105'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>

            <div className="text-sm text-gray-500 order-first sm:order-none">
              {currentStep + 1} of {steps.length}
            </div>

            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center justify-center w-full sm:w-auto px-6 py-4 sm:py-3 rounded-lg font-medium transition-all duration-200 touch-manipulation ${
                !isStepValid()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl hover:scale-105'
              }`}
            >
              {currentStep === steps.length - 1 ? 'Calculate Results' : 'Next'}
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-center text-gray-500 text-sm">
              <span>Powered by</span>
              <img src={TrialConnxLogo} alt="TrialConnx" className="h-4 ml-2" />
            </div>
            <p className="text-gray-400 text-xs mt-2">
              Accelerating clinical research through smart feasibility solutions
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessFlow;